/**
 * OncologyEpisodeLink Component
 * 
 * A reusable component that shows oncology episode linkage for an encounter.
 * Can be embedded in encounter details, inpatient admission, etc.
 * Allows linking/unlinking encounters to oncology episodes of care.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  LocalHospital as HospitalIcon,
  OpenInNew as OpenIcon,
  CheckCircle as ActiveIcon,
  Pause as PauseIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useUserData } from '../../hooks/useUserData';
import {
  getPatientEpisodes,
  getEpisode,
  linkEncounterToEpisode,
  unlinkEncounterFromEpisode
} from '../../services/episodeOfCareService';

// Status color config
const statusConfig = {
  'Active': { color: 'success', icon: <ActiveIcon fontSize="small" /> },
  'On Hold': { color: 'warning', icon: <PauseIcon fontSize="small" /> },
  'Completed': { color: 'info', icon: <ActiveIcon fontSize="small" /> },
  'Cancelled': { color: 'error', icon: <CancelIcon fontSize="small" /> }
};

// Phase color config
const phaseConfig = {
  'Diagnosis': { color: 'default' },
  'Active Treatment': { color: 'primary' },
  'Surveillance': { color: 'info' },
  'Survivorship': { color: 'success' },
  'Palliative': { color: 'warning' },
  'End of Life': { color: 'error' }
};

const OncologyEpisodeLink = ({
  encounterId,
  patientId,
  linkedEpisodeId = null, // If encounter already has an episode linked
  onLinkChange, // Callback when link status changes
  compact = false, // Compact mode for sidebar/smaller spaces
  readOnly = false, // Read-only mode (view only, no link/unlink)
  showCard = true // Whether to wrap in a Card
}) => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const facilityId = defaultFacility?.id;

  // State
  const [linkedEpisode, setLinkedEpisode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableEpisodes, setAvailableEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch linked episode details if we have an ID
  const fetchLinkedEpisode = useCallback(async () => {
    if (!linkedEpisodeId) {
      setLinkedEpisode(null);
      return;
    }

    try {
      setLoading(true);
      const episode = await getEpisode(linkedEpisodeId);
      setLinkedEpisode(episode);
    } catch (err) {
      console.error('Error fetching linked episode:', err);
      setError('Failed to load linked episode');
    } finally {
      setLoading(false);
    }
  }, [linkedEpisodeId]);

  useEffect(() => {
    fetchLinkedEpisode();
  }, [fetchLinkedEpisode]);

  // Fetch available episodes for patient
  const fetchAvailableEpisodes = useCallback(async () => {
    if (!patientId || !facilityId) return;

    try {
      setLoadingEpisodes(true);
      const result = await getPatientEpisodes(patientId, facilityId, { limit: 50 });
      // Filter to only show active episodes that don't already include this encounter
      const episodes = (result.data || []).filter(ep => 
        ep.status === 'Active' || ep.status === 'On Hold'
      );
      setAvailableEpisodes(episodes);
    } catch (err) {
      console.error('Error fetching episodes:', err);
      setError('Failed to load episodes');
    } finally {
      setLoadingEpisodes(false);
    }
  }, [patientId, facilityId]);

  // Handle opening the link dialog
  const handleOpenLinkDialog = () => {
    fetchAvailableEpisodes();
    setDialogOpen(true);
  };

  // Handle linking encounter to episode
  const handleLinkEpisode = async (episode) => {
    try {
      setActionLoading(true);
      setError(null);
      await linkEncounterToEpisode(episode.id, encounterId);
      setLinkedEpisode(episode);
      setDialogOpen(false);
      onLinkChange?.(episode.id);
    } catch (err) {
      console.error('Error linking encounter:', err);
      setError('Failed to link encounter to episode');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle unlinking encounter from episode
  const handleUnlinkEpisode = async () => {
    if (!linkedEpisode) return;

    try {
      setActionLoading(true);
      setError(null);
      await unlinkEncounterFromEpisode(linkedEpisode.id, encounterId);
      setLinkedEpisode(null);
      onLinkChange?.(null);
    } catch (err) {
      console.error('Error unlinking encounter:', err);
      setError('Failed to unlink encounter from episode');
    } finally {
      setActionLoading(false);
    }
  };

  // Navigate to episode details
  const handleViewEpisode = () => {
    if (linkedEpisode?.id) {
      navigate(`/oncology/episodes/${linkedEpisode.id}`);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: compact ? 1 : 2 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading episode info...
        </Typography>
      </Box>
    );
  }

  // Render content
  const renderContent = () => {
    if (linkedEpisode) {
      // Linked to an episode - show details
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TimelineIcon color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  {linkedEpisode.episodeName || 'Oncology Episode'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {linkedEpisode.diagnosisSummary || 'Cancer treatment'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={linkedEpisode.status}
                  color={statusConfig[linkedEpisode.status]?.color || 'default'}
                  icon={statusConfig[linkedEpisode.status]?.icon}
                />
                <Chip
                  size="small"
                  label={linkedEpisode.currentPhase}
                  color={phaseConfig[linkedEpisode.currentPhase]?.color || 'default'}
                  variant="outlined"
                />
              </Box>
              {!compact && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Started: {formatDate(linkedEpisode.startDate)}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Tooltip title="View Episode Details">
                <IconButton size="small" onClick={handleViewEpisode}>
                  <OpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!readOnly && (
                <Tooltip title="Unlink from Episode">
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={handleUnlinkEpisode}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <CircularProgress size={16} /> : <UnlinkIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    // Not linked - show link option
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon color="disabled" sx={{ fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Not linked to any oncology episode
          </Typography>
        </Box>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={handleOpenLinkDialog}
          >
            {compact ? 'Link' : 'Link to Episode'}
          </Button>
        )}
      </Box>
    );
  };

  const content = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {renderContent()}

      {/* Link Episode Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Link to Oncology Episode
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingEpisodes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : availableEpisodes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <HospitalIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No active oncology episodes found for this patient
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create an oncology episode first from the Oncology module.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an episode to link this encounter to:
              </Typography>
              <List sx={{ bgcolor: 'background.paper' }}>
                {availableEpisodes.map((episode, index) => (
                  <React.Fragment key={episode.id}>
                    {index > 0 && <Divider />}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleLinkEpisode(episode)}
                        disabled={actionLoading}
                      >
                        <ListItemIcon>
                          <TimelineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">
                                {episode.episodeName || 'Unnamed Episode'}
                              </Typography>
                              <Chip
                                size="small"
                                label={episode.status}
                                color={statusConfig[episode.status]?.color || 'default'}
                              />
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Typography variant="body2" color="text.secondary" component="span">
                                {episode.diagnosisSummary || 'Cancer treatment'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" component="span">
                                Phase: {episode.currentPhase} • Started: {formatDate(episode.startDate)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Wrap in card if needed
  if (showCard) {
    return (
      <Card variant="outlined" sx={{ mb: compact ? 1 : 2 }}>
        <CardContent sx={{ py: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Oncology Episode
          </Typography>
          {content}
        </CardContent>
      </Card>
    );
  }

  return content;
};

export default OncologyEpisodeLink;
